import TooltipLink from "@/components/TooltipLink/TooltipLink";
import useTranslation from "@/hooks/useTranslation";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { message, Popconfirm, Tooltip } from "antd";
import { ReactNode } from "react";
import { PackagingMaterial } from "./PackagingMaterialOverview/PackagingMaterialsOverview";
import { useDeletePackagingMaterials } from "./usePackagingMaterials";

export default function DeletePackagingMaterialAction({
  children,
  materials,
  onSucces,
  deselectUndeletableProducts,
}: {
  children: ReactNode;
  materials: PackagingMaterial[];
  onSucces?: () => void;
  deselectUndeletableProducts?: () => void;
}) {
  const deleteMaterials = useDeletePackagingMaterials();

  const materialLabel = materials.length === 1 ? "material" : `${materials.length} materials`;
  const deleteText = `Delete ${materialLabel}`;

  const disabled = materials.some((x) => x.packaging_relations > 0 || x.facility_relations > 0);
  const undeletableCount = materials.filter(
    (x) => x.packaging_relations > 0 || x.facility_relations
  ).length;
  const translation = useTranslation();

  return (
    <Popconfirm
      title={deleteText}
      description={
        <>
          <p>Deleting a material also deletes:</p>
          <ul>
            <li>matches to the impact reference</li>
            <li>related unit conversions</li>
          </ul>
          <p style={{ color: "#F5222D" }}>This action can not be undone.</p>
        </>
      }
      onConfirm={() =>
        deleteMaterials
          .mutateAsync({ materialIds: materials.map((x) => x.id) })
          .then((result) => {
            if (result.status === 200) {
              message.success(`Material${materials.length > 1 ? "s" : ""} removed.`);
              onSucces && onSucces();
            }
          })
          .catch(() => message.error(translation.errors.general))
      }
      onCancel={() => {}}
      icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
      okText={deleteText}
      cancelText="Cancel"
      placement="bottom"
      okType="danger"
      okButtonProps={{ loading: deleteMaterials.isLoading }}
      disabled={disabled}
    >
      <Tooltip
        placement="bottom"
        title={
          disabled &&
          (materials.length === 1 ? (
            <p>
              This material can not be deleted as it is referenced in a packaging or facility
              contract. Delete this packaging or contract first to remove this material.
            </p>
          ) : (
            <p>
              {undeletableCount} of the materials you selected are referenced in a packaging or
              facility contract.{" "}
              {deselectUndeletableProducts ? (
                <>
                  <TooltipLink onClick={() => deselectUndeletableProducts()}>
                    Click here
                  </TooltipLink>{" "}
                  to deselect them or remove the related packaging or facility contract to continue.
                </>
              ) : (
                <>Delete the related packaging or facility contract to continue.</>
              )}
            </p>
          ))
        }
      >
        {children}
      </Tooltip>
    </Popconfirm>
  );
}
